import axios from "axios"

import { BaseUser, BaseUserProfile, CliUser, RankedUser, SearchedUser } from "../types/users"
import { GameReturn } from "../types/game"
import { SponsorInfo } from "../types/sponsor"
import tag from "../types/tag"
import { Lore, SortQuestion, SortResults } from "../types/daedalus"
import { BaseTournament, Tournament } from "../types/tournament"
import { APIAuditSetUsernames, APIChatter, APIDaedalusSort, APIDaedalusSubmitKey, APISponsorEditChannel, APISurvey, APITourneyJoin, APIUserRoleUpdate } from "../types/api"
import { ChannelXPReport, HouseReport, RankReport } from "../types/reports"
import { debuglog } from "./utils"
import { Channel, Webhook } from "../types/chatterbox"

export type ErrMsg = {
  msg: string
} | { userIsAuthed: boolean }

export type ret<A> = Promise<A | ErrMsg>

/** /assets/${path} */
export function imgSrc(path: string, url: boolean = false) {
  const p = `${process.env.REACT_APP_BACKEND}/assets/${path}`
  if (url) return `url(${p})`
  else return p
}

function updateRoles(optRoles: APIUserRoleUpdate["roles"], equip: APIUserRoleUpdate["equipped"]): ret<{ roles: CliUser["roles"] }> {
  return call(`user/roles/update`, "patch", { roles: optRoles, equipped: equip })
}

function updateIGNs(newIgns: Record<string, FormDataEntryValue>): ret<{ igns: CliUser["igns"] }> {
  return call(`user/igns/update`, "patch", { igns: newIgns })
}

function userSearch(term: string): ret<SearchedUser[]> {
  if (term.length < 3) return new Promise((res) => res([]))
  return call(`user/search?term=${encodeURIComponent(term)}`, "post")
}

export const chatterboxAPI = {
  getSendables: (): ret<{ webhooks: Webhook[], channels?: Channel[] }> => call("chatterbox/sendables"),
  chatter: (payload: APIChatter, speakerId: string): ret<{messageId: string}> => call(`chatterbox/chatter/${speakerId}`, "post", payload),
  indicator: (channelId: string) => call(`chatterbox/indicator/${channelId}`, "post")
}

export const userAPI = {
  auth: (): ret<CliUser> => call("auth"), //
  logout: (): Promise<{ msg: "Ok." }> => call("auth/logout", "post"), //
  get: (id: string): ret<{ user: BaseUserProfile | null }> => call(`user/${id}`), //
  getMain: (id: string): ret<{ user: CliUser | null }> => call(`user/${id}`), //
  kick: (id: string, reason: string): ret<{kicked: true}> => call(`user/${id}/kick`, "post", { reason }),
  search: userSearch,
  updateRoles,
  updateIGNs,
}

export const gameAPI = {
  list: (): ret<GameReturn> => call("games"),
  purchase: (code: string): ret<GameReturn> => call(`games/buy/${code}`, "post")
}

type xpProps = Pick<CliUser, "xp" | "xpSetting" | "level" | "toNextLevel">

export const leaderboardAPI = {
  getLeaderboard: (withXp: boolean): ret<{user: xpProps, season: RankedUser[], life: RankedUser[] }> => call(`/leaderboard?withXp=${withXp}`, "get"),
  enableXP: (level: string): ret<{user: xpProps, leaderboard: {season: RankedUser[], life: RankedUser[]}}> => call(`/leaderboard/opt/${level}`, "patch"),
}

export const sponsorAPI = {
  getInfo: (): ret<SponsorInfo> => call("sponsor"),
  editChannel: (channel: APISponsorEditChannel): ret<SponsorInfo> => call("sponsor/edit/channel", "patch", channel),
  editEmoji: (form: FormData): ret<SponsorInfo> => call("sponsor/edit/emoji", "patch", form),
  setup: (): ret<SponsorInfo> => call("sponsor/setup")
}

export const tagAPI = {
  get: (): ret<tag[]> => call("tags"),
  manage: (tag: FormData): ret<tag> => call("tags/manage", "post", tag),
  delete: (name: string): ret<tag[]> => call(`tags/${name}/delete`, "delete")
}

export const auditAPI = {
  getUsernames: (): ret<BaseUser[]> => call("audits/usernames"),
  setUsernames: (users: APIAuditSetUsernames["users"]): ret<BaseUser[]> => call("audits/usernames/edit", "patch", { users }),
  getAvatars: (): ret<BaseUser[]> => call("audits/avatars")
}

export const daedalusAPI = {
  submitKey: (code: APIDaedalusSubmitKey["code"]): ret<{result: boolean | string, lore: Lore[]}> => call("daedalus/code", "post", { code }),
  getQuestions: (): ret<SortQuestion[]> => call("daedalus/questions"),
  sortMe: (answers: APIDaedalusSort): ret<SortResults> => call("daedalus/sort", "post", answers),
  getLore: (): ret<Lore[]> => call("daedalus/lore")
}

export const tourneyAPI = {
  // administrative
  create: (tourney: FormData): ret<{ id: string }> => call("tournaments/create", "post", tourney),
  edit: (id: string, tourney: FormData): ret<Tournament> => call(`tournaments/${id}/edit`, "patch", tourney),
  delete: (id: string): ret<{ status: true }> => call(`tournaments/${id}/delete`, "delete"),
  // discovery and setup
  getList: (): ret<{ past: BaseTournament[], present: BaseTournament[] }> => call("tournaments/list"),
  get: (id: string): ret<Tournament> => call(`tournaments/${id}`),
  join: (id: string, user: APITourneyJoin): ret<Tournament> => call(`tournaments/${id}/join`, "patch", user),
  leave: (id: string): ret<Tournament> => call(`tournaments/${id}/leave`, "patch"),
  // round related
  forfeit: (id: string): ret<Tournament> => call(`tournaments/${id}/forfeit`),
  checkin: (id: string): ret<Tournament> => call(`tournaments/${id}/checkin`, "post"),
}

export const reportAPI = {
  getChannelXP: (): ret<ChannelXPReport> => call("reports/channelXp"),
  getRanks: (): ret<RankReport[]> => call("reports/ranks"),
  fixRanks: (ids: string[]): ret<RankReport[]> => call("reports/ranks/fix", "patch", ids),
  getHouses: (): ret<HouseReport[]> => call("reports/houses")
}

export const surveyAPI = {
  send: (survey: APISurvey): ret<{ result: boolean }> => call("survey", "post", survey)
}

export function call(url: string, method: "get"|"post"|"patch"|"delete" = "get", data?: any): Promise<any> {
  console.log(process.env)
  return axios({ url: `${process.env.REACT_APP_BACKEND}/api/${url}`, method, withCredentials: true, data }).then((res) => {
    debuglog(method.toUpperCase(), url, data ?? {}, "--->", res?.data)
    return res.data
  }).catch((res) => {
    debuglog(res)
    if (res.status === 401) return { userIsAuthed: false }
    if (res.status === 404) return { msg: "I ran into a problem with finding the API site." }
    if (res.status === 429) return { msg: "You're going too fast! Try again in a bit!" }
    if (res.headers?.get('content-type').includes('text/html')) return { msg: "I ran into a problem in the backend!" }
    if (typeof res?.response?.data === "object" && "msg" in res?.response?.data) return res.response.data;
    return { msg: "I ran into a problem in the backend!" };
  })
}